// pages/ProductsPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, MoreVertical, Package, Filter, X, Award, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import ProductsList from "../../../components/features/products/ProductsList";
import { useProducts } from "../../../hooks/products/useProducts";
import { useAuth } from "../../../hooks/auth/useAuth";

export default function ProductsPage() {
  const navigate = useNavigate();
  const { user } = useAuth()
  const role = user?.role
  
  // La pagination est maintenant gérée dans le hook
  const { 
    products, 
    deleteProduct, 
    deleteManyProducts,
    total,
    pages,
    currentPage,
    goToPage,
    nextPage,
    prevPage,
    loading
  } = useProducts();



  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedQuality, setSelectedQuality] = useState<string>("");
  const [selectedTexture, setSelectedTexture] = useState<string>("");
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [productToDelete, setProductToDelete] = useState<any | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Récupérer les types uniques (depuis tous les produits, pas juste la page courante)
  // Note: Vous devriez peut-être faire une requête séparée pour tous les filtres
  const typeOptions = useMemo(() => {
    const types = new Set(products.map(p => p.type).filter(Boolean));
    return Array.from(types);
  }, [products]);

  const qualityOptions = useMemo(() => {
    const qualities = new Set(products.map(p => p.quality).filter(Boolean));
    return Array.from(qualities);
  }, [products]);

  const textureOptions = useMemo(() => {
    const textures = new Set(products.map(p => p.texture).filter(Boolean));
    return Array.from(textures);
  }, [products]);

  // Filtrage des produits (client-side)
  const filteredProducts = useMemo(() => {
    let result = products;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(searchLower) ||
        p.type?.toLowerCase().includes(searchLower) ||
        p.quality?.toLowerCase().includes(searchLower) ||
        p.texture?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedType) {
      result = result.filter(p => p.type === selectedType);
    }

    if (selectedQuality) {
      result = result.filter(p => p.quality === selectedQuality);
    }

    if (selectedTexture) {
      result = result.filter(p => p.texture === selectedTexture);
    }

    return result;
  }, [products, searchTerm, selectedType, selectedQuality, selectedTexture]);

  // Statistiques (sur tous les produits)
  const stats = useMemo(() => ({
    total: products.length,
    totalValue: products.reduce((acc, p) => acc + (p.price || 0), 0),
    withBadge: products.filter(p => p.badge).length
  }), [products]);

  const handleDelete = async () => {
    if (!productToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteProduct(productToDelete.id);
      setProductToDelete(null);
      alertSuccess("Produit supprimé avec succès");
    } catch (err) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    try {
      await deleteManyProducts(itemsToDelete);
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} produit(s) supprimé(s)`);
    } catch (err) {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedType("");
    setSelectedQuality("");
    setSelectedTexture("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedType || selectedQuality || selectedTexture || searchTerm;

  // Générer les numéros de page à afficher
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pages, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  return (
    <PageLayout
      title="Gestion des produits"
      description={`${total} produit${total > 1 ? 's' : ''} au total • ${filteredProducts.length} affiché${filteredProducts.length > 1 ? 's' : ''} sur cette page`}
      actions={
        <FlexContainer className="animate-fade-in-up w-full">
          <button
            onClick={() => { navigate(`/${role}/products/new`); }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
          >
            <Plus size={18} /> Ajouter
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Filter size={18} /> Filtrer
            {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
          </button>
        </FlexContainer>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-blue-700/70">Total produits</p>
          <p className="text-2xl font-bold text-blue-700">{total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-green-700/70">Valeur du catalogue</p>
          <p className="text-2xl font-bold text-green-700">{stats.totalValue.toLocaleString('fr-FR')} FCFA</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-orange-700/70">Avec badge</p>
          <p className="text-2xl font-bold text-orange-700">{stats.withBadge}</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par nom, type, qualité ou texture..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">Filtres</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                <X size={14} /> Effacer tout
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Package size={14} /> Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tous les types</option>
                {typeOptions.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Award size={14} /> Qualité
              </label>
              <select
                value={selectedQuality}
                onChange={(e) => setSelectedQuality(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Toutes les qualités</option>
                {qualityOptions.map(quality => (
                  <option key={quality} value={quality}>{quality}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Sparkles size={14} /> Texture
              </label>
              <select
                value={selectedTexture}
                onChange={(e) => setSelectedTexture(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Toutes les textures</option>
                {textureOptions.map(texture => (
                  <option key={texture} value={texture}>{texture}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <ProductsList
            products={filteredProducts}
            selectable={true}
            onAction={(p) => setSelectedProduct(p)}
            selectActions={[{
              label: "Supprimer",
              onClick: (selected) => setItemsToDelete(selected.map(i => i.id)),
              className: "bg-red-600 text-white"
            }]}
          />
          
          {/* Pagination */}
          {pages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Page {currentPage} sur {pages}</span>
                <span className="text-gray-400">•</span>
                <span>{total} produit{total > 1 ? 's' : ''}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`w-10 h-10 rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-primary text-white font-medium'
                        : 'hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={nextPage}
                  disabled={currentPage === pages}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Menu modal */}
      {selectedProduct && (
        <MenuModal
          title={selectedProduct.name}
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          icon={<Package className="text-primary" size={20} />}
          menu={[
            { label: "Voir détails", icon: Search, onClick: () => navigate(`/${role}/products/details?id=${selectedProduct.id}`) },
            { label: "Modifier", icon: Package, onClick: () => navigate(`/${role}/products/update?id=${selectedProduct.id}`) },
            { label: "Supprimer", icon: MoreVertical, onClick: () => { setProductToDelete(selectedProduct); setSelectedProduct(null); } }
          ]}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!productToDelete}
        onClose={() => setProductToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le produit"
        message={`Supprimer "${productToDelete?.name}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} produit(s) ?`}
      />
    </PageLayout>
  );
}